import { FieldHelperMessages } from 'components/FieldHelperMessages';
import {
  RichTextEditor,
  RichTextEditorRefContent,
} from 'components/RichTextEditor';
import { NotificationType } from 'features/notifications/notifications.types';
import { useReportSnippets } from 'features/reports/hooks/useReportSnippets';
import {
  useReportWarningWords,
  ReportWarningWordsByCategory,
} from 'features/reports/hooks/useReportWarningWords';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { isNil } from 'es-toolkit/compat';
import { removeHTMLTags } from 'shared/utils';
import {
  applyHighlights,
  createComplexKeywordsRegex,
  getReportValidationResult,
  removeDecorationMarks,
} from './ReportMessageField.utils';
import {
  KnownValidationRuleId,
  ReportMessageFieldCurrentValues,
  ReportMessageFieldInitialValues,
  ValidationRule,
} from './ReportMessageField.types';

const API_CATEGORY_RULE_IDS: KnownValidationRuleId[] = [
  'catchall',
  'injuryFatality',
  'cause',
  'political',
  'evacuation',
];

const getApiDrivenRules = (
  warningWordsByCategory: ReportWarningWordsByCategory,
): ValidationRule[] => {
  const configs: Array<{
    category: keyof ReportWarningWordsByCategory;
    id: KnownValidationRuleId;
    messageKey: string;
    condition: ValidationRule['condition'];
  }> = [
    {
      category: 'catchall',
      id: 'catchall',
      messageKey: 'addIncidentReport.inputs.description.warnings.catchall',
      condition: () => true,
    },
    {
      category: 'injury_fatality',
      id: 'injuryFatality',
      messageKey:
        'addIncidentReport.inputs.description.warnings.injuryFatality',
      condition: () => true,
    },
    {
      category: 'cause',
      id: 'cause',
      messageKey: 'addIncidentReport.inputs.description.warnings.cause',
      condition: () => true,
    },
    {
      category: 'political',
      id: 'political',
      messageKey: 'addIncidentReport.inputs.description.warnings.political',
      condition: () => true,
    },
    {
      category: 'evacuation',
      id: 'evacuation',
      messageKey: 'addIncidentReport.inputs.description.warnings.evacuation',
      condition: ({ isSilent }) => Boolean(isSilent),
    },
  ];

  return configs
    .filter(({ category }) => warningWordsByCategory[category].length > 0)
    .map(({ category, id, messageKey, condition }) => ({
      id,
      condition,
      messageKey,
      regex: createComplexKeywordsRegex(warningWordsByCategory[category]),
    }));
};

type ReportMessageFieldProps = {
  name: string;
  initialValue: string;
  /** Snapshot of the incident's values when the form opened — used for change detection. */
  initialValues?: ReportMessageFieldInitialValues;
  /** Live form values the validation rules read from. */
  currentValues: ReportMessageFieldCurrentValues;
  helperText?: string;
  /** Memoized array */
  customValidationRules?: ValidationRule[];
  warningRulesToSkip?: KnownValidationRuleId[];
  plainTextFieldName?: string;
  isCritical?: boolean;
};

export const ReportMessageField = (
  props: ReportMessageFieldProps,
): JSX.Element => {
  const {
    name,
    initialValue,
    initialValues,
    currentValues,
    helperText,
    customValidationRules,
    warningRulesToSkip,
    plainTextFieldName,
    isCritical = false,
  } = props;
  const { control, formState, setValue } = useFormContext();
  const { t } = useTranslation();
  const { snippets } = useReportSnippets();
  const isApiWarningWordsEnabled = useReleaseFlag(
    'use_report_warning_words_api',
  );
  const { warningWordsByCategory } = useReportWarningWords();
  const editorRef = useRef<RichTextEditorRefContent>(null);
  const [messageText, setMessageText] = useState(() =>
    removeHTMLTags(initialValue),
  );
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);

  const acreageChanged =
    !isNil(currentValues.acreage) &&
    currentValues.acreage !== initialValues?.acreage;
  const containmentChanged =
    !isNil(currentValues.containment) &&
    currentValues.containment !== initialValues?.containment;

  const apiDrivenRules = useMemo((): ValidationRule[] => {
    if (!isApiWarningWordsEnabled) return [];
    return getApiDrivenRules(warningWordsByCategory);
  }, [isApiWarningWordsEnabled, warningWordsByCategory]);

  const validateReportMessage = useCallback(
    (message: string) => {
      const { messageWarnings, highlightInstructions } =
        getReportValidationResult({
          t,
          rulesToSkip: isApiWarningWordsEnabled
            ? [...API_CATEGORY_RULE_IDS, ...(warningRulesToSkip ?? [])]
            : warningRulesToSkip,
          customRules: isApiWarningWordsEnabled
            ? [...apiDrivenRules, ...(customValidationRules ?? [])]
            : customValidationRules,
          evaluationInputData: {
            report: message,
            fpsChecked: currentValues.isFps,
            isSilent:
              currentValues.notificationType === NotificationType.Silent,
            acreageChanged,
            containmentChanged,
            isPrescribed: currentValues.isPrescribed,
          },
        });

      setValidationWarnings(messageWarnings);

      if (editorRef.current?.editor) {
        applyHighlights(editorRef.current.editor, highlightInstructions);
      }
    },
    [
      acreageChanged,
      containmentChanged,
      apiDrivenRules,
      currentValues.isFps,
      currentValues.notificationType,
      currentValues.isPrescribed,
      customValidationRules,
      isApiWarningWordsEnabled,
      t,
      warningRulesToSkip,
    ],
  );

  const debouncedValidateReportMessage = useDebounceCallback(
    validateReportMessage,
    350,
  );

  // Use useEffect instead of onChange to avoid infinite loops.
  // applyHighlights mutates editor state, which retriggers onChange.
  // We only track raw text (messageText), not HTML markup.
  useEffect(() => {
    debouncedValidateReportMessage(messageText);

    return () => debouncedValidateReportMessage.cancel();
  }, [debouncedValidateReportMessage, messageText]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }): JSX.Element => {
        const helperMessages = [];
        if (fieldState.error?.message) {
          helperMessages.push(fieldState.error.message);
        } else if (validationWarnings.length) {
          helperMessages.push(...validationWarnings);
        } else if (helperText) {
          helperMessages.push(helperText);
        }

        return (
          <div>
            <RichTextEditor
              id="field-control-report-message"
              data-testid="report-message"
              ref={editorRef}
              label={t(
                isCritical
                  ? 'createEditWildfire.inputs.reportMessage.labelCritical'
                  : 'createEditWildfire.inputs.reportMessage.label',
              )}
              initialValue={initialValue}
              onChange={(html, text) => {
                setMessageText(text);
                field.onChange(removeDecorationMarks(html));
                if (plainTextFieldName) {
                  setValue(plainTextFieldName, text);
                }
              }}
              editable={!formState.isSubmitting}
              error={Boolean(fieldState.error)}
              required
              warning={Boolean(validationWarnings.length)}
              onBlur={field.onBlur}
              snippets={snippets}
            />
            {Boolean(helperMessages.length) && (
              <FieldHelperMessages
                messages={helperMessages}
                error={Boolean(fieldState.error)}
                sx={{
                  mx: 1.75,
                  mt: 0.5,
                  color: validationWarnings.length ? 'warning.main' : undefined,
                }}
                data-testid="report-validation-message"
              />
            )}
          </div>
        );
      }}
    />
  );
};
